import { Alert, Flex, Form, List, Space, Tag, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"

import Button from "../../../../../../Components/Button"
import InputNumber from "../../../../../../Components/Input/InputNumber"
import Icon from "../../../../../../Icon"

import noteStyles from "../inlineNote.module.less"
import styles from "./editNumberForm.module.less"

const { Text } = Typography

// Same modal shell across all three edit popups (mirrors prevent's
// ChangeRefundForm). A small per-field icon at the top + per-field info text
// + per-field bordered list rows is what distinguishes them. Keeping the
// structure consistent matches the rest of the platform.
const FIELD_META = {
  max_auto_approve_amount: {
    label: "Maximum auto-approve amount",
    headIcon: "money",
    addonBefore: "$",
    min: 0,
    step: 10,
    precision: 2,
    placeholder: "250.00",
    infoIcon: "circle_info",
    infoText:
      "Auto-approve only fires when the disputed amount sits at or below this limit. Larger disputes always wait in your review queue.",
    rows: [
      {
        label: "$0 to limit",
        tag: { text: "Auto-approved", type: "success", icon: "clock_alert" }
      },
      {
        label: "Above the limit",
        tag: { text: "Awaiting review", type: "warning", icon: "hour_glass" }
      }
    ]
  },
  ignore_below_amount: {
    label: "Ignore disputes below this amount",
    headIcon: "money",
    addonBefore: "$",
    min: 0,
    step: 5,
    precision: 2,
    placeholder: "20.00",
    infoIcon: "circle_info",
    // No info line here on purpose. The modal's own subtitle (MODAL_DESCRIPTIONS in
    // the Preferences page) already spells out that these disputes are NOT closed,
    // just not auto-submitted and marked Skipped, so a second paragraph saying the
    // same thing only crowds the popup.
    infoText: null,
    // Precedence note, shown as the primary callout (same one the Preferences page
    // carries under this setting) so the rule reads the same in both places.
    overridesNote: true,
    rows: [
      { label: "Below this amount", tag: { text: "Skipped", type: "default", icon: "hour_glass" } },
      {
        label: "At or above",
        tag: { text: "Fought as normal", type: "success", icon: "clock_alert" }
      }
    ]
  },
  repeat_disputer_threshold: {
    label: "Repeat-disputer threshold",
    headIcon: "arrows_repeat",
    addonAfter: "Disputes",
    // 0 turns the feature off entirely (no repeat-disputer routing).
    min: 0,
    max: 20,
    step: 1,
    precision: 0,
    placeholder: "2",
    infoIcon: "circle_info",
    infoText:
      "Counts disputes filed by the same customer email across all time. Useful for spotting fraud patterns and frequent flyers before they cost you another loss. Set 0 to turn this off.",
    rows: [
      {
        label: "At or above the threshold",
        tag: { text: "Awaiting review", type: "warning", icon: "hour_glass" }
      },
      {
        label: "Below the threshold",
        tag: { text: "Auto-approved", type: "success", icon: "clock_alert" }
      },
      {
        label: "First-time disputer",
        tag: { text: "Auto-approved", type: "success", icon: "clock_alert" }
      }
    ]
  }
}

function EditNumberForm({ fieldKey, initial, onSubmit, onClose }) {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const meta = FIELD_META[fieldKey] || FIELD_META.max_auto_approve_amount

  const onFinish = (values) => {
    const v = Number(values.amount)
    if (Number.isNaN(v)) return
    if (meta.max != null && v > meta.max) return
    if (meta.min != null && v < meta.min) return
    onSubmit(v)
  }

  return (
    <Form
      onFinish={onFinish}
      validateTrigger="onSubmit"
      layout="vertical"
      form={form}
      initialValues={{ amount: Number(initial) || 0 }}
      requiredMark={false}
      style={{ marginTop: 24 }}
    >
      {/* Small per-field flourish: contextual icon above the input so the
          three modals don't read as carbon copies. Same shell, different
          glyph + different list rows (defined in meta above). */}
      {meta.headIcon ? (
        <Flex justify="center" style={{ marginBottom: 16 }}>
          <span className={styles.headIcon}>
            <Icon type={meta.headIcon} style={{ fontSize: 24 }} />
          </span>
        </Flex>
      ) : null}

      <Form.Item label={t(meta.label)} name="amount" style={{ marginBottom: 8 }}>
        <InputNumber
          addonBefore={meta.addonBefore ? <Text type="secondary">{meta.addonBefore}</Text> : null}
          addonAfter={meta.addonAfter ? <Text type="secondary">{t(meta.addonAfter)}</Text> : null}
          min={meta.min}
          max={meta.max}
          step={meta.step}
          precision={meta.precision}
          placeholder={meta.placeholder}
          style={{ width: "100%" }}
          autoFocus
        />
      </Form.Item>

      <Form.Item noStyle>
        {meta.infoText ? (
          <Space size={4} align="start">
            <Icon type={meta.infoIcon} className={styles.infoIcon} style={{ fontSize: 16 }} />
            <Text type="secondary">{t(meta.infoText)}</Text>
          </Space>
        ) : null}

        {meta.overridesNote ? (
          <Alert
            className={`${noteStyles.note} ${noteStyles.notePrimary}`}
            showIcon
            icon={<Icon type="circle_info" style={{ fontSize: 20 }} />}
            message={<Text>{t("This rule overrides all other rules.")}</Text>}
            style={{ marginTop: 16 }}
          />
        ) : null}

        <List
          size="small"
          header={null}
          footer={null}
          bordered
          dataSource={meta.rows}
          renderItem={(item) => (
            <List.Item
              extra={
                <Tag
                  bordered={false}
                  icon={
                    item?.tag?.icon ? (
                      <Icon
                        type={item.tag.icon}
                        className={styles.tagIcon}
                        style={{ fontSize: 16 }}
                      />
                    ) : null
                  }
                  color={item.tag.type}
                  style={{ margin: 0, padding: "4px 8px", display: "flex", alignItems: "center" }}
                  className={cls("color-text", {
                    only_color_text: !item.tag.icon,
                    disabled: item.tag.type === "disabled"
                  })}
                >
                  <Text>{t(item.tag.text)}</Text>
                </Tag>
              }
            >
              <Text strong>{t(item.label)}</Text>
            </List.Item>
          )}
          style={{ marginTop: 16 }}
        />
      </Form.Item>

      <Flex gap={16} justify="end" style={{ padding: "0 4px 4px 0", marginTop: 40 }}>
        <Button size="large" htmlType="button" onClick={onClose} text={t("Cancel")} />
        <Button size="large" type="primary" htmlType="submit" text={t("Confirm")} />
      </Flex>
    </Form>
  )
}

export default EditNumberForm
